import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AppProvider, useApp } from "@/context/AppContext";
import { Loader } from "@/components/Loader";
import { routeForUser } from "@/lib/nav";

import Entry from "@/pages/Entry";
import Login from "@/pages/Login";
import Apply from "@/pages/Apply";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Corporate from "@/pages/Corporate";
import Status from "@/pages/Status";
import Verify from "@/pages/Verify";

import MemberLayout from "@/pages/member/MemberLayout";
import MemberHome from "@/pages/member/Home";
import MyCard from "@/pages/member/MyCard";
import Events from "@/pages/member/Events";
import News from "@/pages/member/News";
import Profile from "@/pages/member/Profile";
import Membership from "@/pages/member/Membership";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminMembers from "@/pages/admin/Members";
import AdminEvents from "@/pages/admin/Events";
import AdminNews from "@/pages/admin/News";
import AdminSettings from "@/pages/admin/Settings";

function FullLoader() {
  return <div className="min-h-screen flex items-center justify-center bg-[color:var(--cu-black)]"><Loader /></div>;
}

function RequireAuth({ children }) {
  const { user } = useApp();
  if (user === undefined) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function RequireActive({ children }) {
  const { user } = useApp();
  if (user === undefined) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "admin") return <Navigate to="/admin" replace />;
  if (user.status !== "active") return <Navigate to="/status" replace />;
  return children;
}

function RequireAdmin({ children }) {
  const { user } = useApp();
  if (user === undefined) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/app/home" replace />;
  return children;
}

function App() {
  return (
    <div className="App cu-grain">
      <AppProvider>
        <BrowserRouter>
          <Toaster theme="dark" position="top-center" richColors toastOptions={{ style: { background: "#141210", border: "1px solid rgba(212,175,55,0.3)", color: "#F4F1EA" } }} />
          <Routes>
            <Route path="/" element={<Entry />} />
            <Route path="/login" element={<Login />} />
            <Route path="/apply" element={<Apply />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/corporate" element={<Corporate />} />
            <Route path="/verify/:code" element={<Verify />} />
            <Route path="/status" element={<RequireAuth><Status /></RequireAuth>} />

            <Route path="/app" element={<RequireActive><MemberLayout /></RequireActive>}>
              <Route index element={<Navigate to="/app/home" replace />} />
              <Route path="home" element={<MemberHome />} />
              <Route path="card" element={<MyCard />} />
              <Route path="events" element={<Events />} />
              <Route path="news" element={<News />} />
              <Route path="profile" element={<Profile />} />
              <Route path="membership" element={<Membership />} />
            </Route>

            <Route path="/admin" element={<RequireAdmin><AdminLayout /></RequireAdmin>}>
              <Route index element={<AdminDashboard />} />
              <Route path="members" element={<AdminMembers />} />
              <Route path="events" element={<AdminEvents />} />
              <Route path="news" element={<AdminNews />} />
              <Route path="settings" element={<AdminSettings />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AppProvider>
    </div>
  );
}

export default App;
